import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import palette from 'theme/palette'

import { LABELS, getTranslationKey, DISEASE_GROUP_TRANSLATION } from '../constants'
import {
  MOBILE_FONT_SIZE,
  PINNED_COL1_WIDTH,
  PINNED_COL2_WIDTH,
  PINNED_COL3_WIDTH,
  tableStyles,
} from '../styles'
import { EditableCell } from './EditableCell'

interface RowData {
  group: string
  subGroup?: string
  label: string
  values: (number | string)[]
  editable: boolean
  id: number[]
  showGroup: boolean
  groupSpan: number
  showSubGroup: boolean
  subGroupSpan: number
  isGroupMiddle: boolean
  isSubGroupMiddle: boolean
  isLastInGroup: boolean
  isLastInSubGroup: boolean
}

interface VirtualRowProps {
  row: RowData
  rowHeight: number
  translateY: number
  disabledColumns: Set<number>
  // NK-273: per-column colour limit, see buildColorThresholds.
  colorThresholds?: (number | null)[]
  onCellChange: (params: {
    group: string
    subGroup?: string
    label: string
    id: number
    columnIndex: number
    value: string
  }) => Promise<void>
  editedCells?: Map<string, string>
  isMobile?: boolean
}

const getLabelBackgroundColor = (label: string) => {
  const labelLower = label.toLowerCase()
  if (labelLower === LABELS.YELLOW.toLowerCase()) return palette.light.warning.main
  if (labelLower === LABELS.GREEN.toLowerCase()) return palette.light.primary.main
  if (labelLower === LABELS.BLACK.toLowerCase()) return palette.light.grey[500]
  if (labelLower === LABELS.GREEN_YELLOW.toLowerCase()) return palette.light.success.light
  return palette.light.grey[100]
}

export const VirtualRow = memo(
  ({
    row,
    rowHeight,
    translateY,
    disabledColumns,
    colorThresholds,
    onCellChange,
    editedCells,
    isMobile,
  }: VirtualRowProps) => {
    const { t } = useTranslation('pondOverview')
    const isAgeRow = row.label.toLowerCase().includes(LABELS.AGE.toLowerCase())

    const handleCellChangeByColumn = useMemo(() => {
      return row.values.map(
        (_, colIdx) => (newVal: string) =>
          onCellChange({
            group: row.group,
            subGroup: row.subGroup,
            label: row.label,
            id: row.id[colIdx],
            columnIndex: colIdx,
            value: newVal,
          })
      )
    }, [onCellChange, row.group, row.subGroup, row.label, row.id])

    const translations = useMemo(
      () => ({
        group: DISEASE_GROUP_TRANSLATION[row.group]
          ? t(DISEASE_GROUP_TRANSLATION[row.group])
          : row.group,
        subGroup: row.subGroup ? t(getTranslationKey(row.subGroup, 'subGroup')) : '',
        label: t(getTranslationKey(row.label, 'label')),
      }),
      [row.group, row.subGroup, row.label, t]
    )

    const cellsData = useMemo(() => {
      return row.values.map((val, colIdx) => {
        const isColumnDisabled = disabledColumns.has(colIdx)
        const isCellEditable = row.editable && !isColumnDisabled
        const cellId = row.id[colIdx]
        const cellKey = `${row.group}_${row.subGroup || 'no-subgroup'}_${row.label}_${cellId}`
        const editedValue = editedCells?.get(cellKey)
        const displayValue = editedValue !== undefined ? editedValue : val ?? '-'

        return {
          displayValue,
          isCellEditable,
          cellId,
          colIdx,
        }
      })
    }, [
      row.values,
      row.id,
      row.group,
      row.subGroup,
      row.label,
      row.editable,
      disabledColumns,
      editedCells,
    ])

    return (
      <div
        style={{
          ...tableStyles.row,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: `${rowHeight}px`,
          transform: `translateY(${translateY}px)`,
        }}
      >
        <div
          style={{
            ...tableStyles.stickyCol1,
            ...(row.isGroupMiddle ? tableStyles.groupCell : {}),
            borderRight: `1.5px solid ${palette.light.grey[300]}`,
            borderBottom: row.isLastInGroup ? `1.5px solid ${palette.light.grey[300]}` : 'none',
            fontSize: isMobile ? MOBILE_FONT_SIZE : undefined,
            fontWeight: isMobile ? 700 : undefined,
          }}
        >
          {row.isGroupMiddle ? translations.group : ''}
        </div>

        {isAgeRow ? (
          <div
            style={{
              position: 'sticky' as const,
              left: `${PINNED_COL1_WIDTH}px`,
              zIndex: 6,
              width: `${PINNED_COL2_WIDTH + PINNED_COL3_WIDTH}px`,
              minWidth: `${PINNED_COL2_WIDTH + PINNED_COL3_WIDTH}px`,
              flexBasis: `${PINNED_COL2_WIDTH + PINNED_COL3_WIDTH}px`,
              ...tableStyles.groupCell,
              color: palette.light.grey[600],
              borderRight: `4px solid ${palette.light.grey[300]}`,
              borderBottom: `1.5px solid ${palette.light.grey[300]}`,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontSize: isMobile ? MOBILE_FONT_SIZE : undefined,
              fontWeight: isMobile ? 700 : undefined,
            }}
          >
            {translations.label}
          </div>
        ) : (
          <>
            <div
              style={{
                ...tableStyles.stickyCol2,
                ...(row.isSubGroupMiddle ? tableStyles.groupCell : {}),
                borderRight: `1.5px solid ${palette.light.grey[300]}`,
                borderBottom: row.isLastInSubGroup
                  ? `1.5px solid ${palette.light.grey[300]}`
                  : 'none',
                fontSize: isMobile ? MOBILE_FONT_SIZE : undefined,
                fontWeight: isMobile ? 700 : undefined,
              }}
            >
              {row.isSubGroupMiddle ? translations.subGroup : ''}
            </div>

            <div
              style={{
                ...tableStyles.cell,
                ...tableStyles.stickyCol3,
                backgroundColor: getLabelBackgroundColor(row.label),
                borderRight: `4px solid ${palette.light.grey[300]}`,
                borderBottom: `1.5px solid ${palette.light.grey[300]}`,
                fontSize: isMobile ? MOBILE_FONT_SIZE : undefined,
                fontWeight: isMobile ? 700 : undefined,
              }}
            >
              {translations.label}
            </div>
          </>
        )}

        {cellsData.map((cell) => (
          <div key={cell.colIdx} style={tableStyles.cell}>
            <EditableCell
              value={cell.displayValue}
              onChange={handleCellChangeByColumn[cell.colIdx]}
              editable={cell.isCellEditable}
              label={row.label}
              group={row.group}
              subGroup={row.subGroup}
              id={cell.cellId}
              columnIndex={cell.colIdx}
              isMobile={isMobile}
              colorThreshold={colorThresholds?.[cell.colIdx]}
            />
          </div>
        ))}
      </div>
    )
  },
  (prev, next) => {
    if (prev.translateY !== next.translateY) return false
    if (prev.rowHeight !== next.rowHeight) return false
    if (prev.row.group !== next.row.group) return false
    if (prev.row.subGroup !== next.row.subGroup) return false
    if (prev.row.label !== next.row.label) return false

    if (prev.row.values.length !== next.row.values.length) return false
    for (let i = 0; i < prev.row.values.length; i++) {
      if (prev.row.values[i] !== next.row.values[i]) return false
    }

    if (prev.disabledColumns.size !== next.disabledColumns.size) return false

    const prevLimits = prev.colorThresholds ?? []
    const nextLimits = next.colorThresholds ?? []
    if (prevLimits.length !== nextLimits.length) return false
    for (let i = 0; i < prevLimits.length; i++) {
      if (prevLimits[i] !== nextLimits[i]) return false
    }

    if (prev.editedCells !== next.editedCells) {
      return false
    }

    return true
  }
)

VirtualRow.displayName = 'VirtualRow'
